import React, { useState, useEffect } from 'react';
import { 
  Camera, Plus, Search, Edit, Trash2, RefreshCw, 
  AlertCircle, CheckCircle, XCircle, Image as ImageIcon,
  Filter, ChevronDown, ChevronRight, ImageOff, Clock, Ban,
  Radio, Wifi, WifiOff, Zap
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, 
  DialogFooter, DialogDescription 
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { toast } from 'sonner';
import api from '@/lib/api';

const TOWER_BASE_URL = 'https://tower.railstream.net';

// Default tier options - used if API doesn't provide tier_options
const DEFAULT_TIER_OPTIONS = [
  { value: 'free', label: 'Free', description: 'Free tier - no membership required' },
  { value: 'fireman', label: 'Fireman', description: 'Basic membership tier' },
  { value: 'conductor', label: 'Conductor', description: 'Included with Engineer memberships' },
  { value: 'engineer', label: 'Engineer', description: 'Premium membership tier' },
];

/**
 * Normalize tier value to string - handles both string and object formats
 * @param {string|object} tier - The tier value (string or {value, label} object)
 * @returns {string} - The tier string value
 */
const normalizeTierValue = (tier) => {
  if (!tier) return 'fireman';
  if (typeof tier === 'string') return tier;
  if (typeof tier === 'object') {
    return tier.value || tier.id || 'fireman';
  }
  return 'fireman';
};

/**
 * Get display label for a tier value
 * @param {string|object} tier - The tier value
 * @param {string} tierLabel - Optional tier_label from API
 * @returns {string} - Human-readable tier label
 */
const getTierDisplayLabel = (tier, tierLabel) => {
  // If we have a tier_label from API, use it
  if (tierLabel && typeof tierLabel === 'string') {
    return tierLabel;
  }
  
  // Handle object tier (shouldn't happen but be safe)
  if (tier && typeof tier === 'object') {
    if (tier.label) return tier.label;
    if (tier.value) {
      const option = DEFAULT_TIER_OPTIONS.find(t => t.value === tier.value);
      return option?.label || tier.value;
    }
    return 'Unknown';
  }
  
  // Handle string tier
  if (typeof tier === 'string') {
    const option = DEFAULT_TIER_OPTIONS.find(t => t.value === tier);
    return option?.label || tier.charAt(0).toUpperCase() + tier.slice(1);
  }
  
  return 'Fireman';
};

// Primary stream fields - shown by default
const PRIMARY_STREAM_FIELDS = [
  { 
    key: 'default_hls', 
    label: 'Default HLS', 
    required: true,
    hint: 'Required. Fallback stream for all clients if no override is set.'
  },
  { 
    key: 'web_hls', 
    label: 'Website HLS', 
    required: false,
    hint: 'Optional. Override for web browser clients. Uses Default HLS if blank.'
  },
  { 
    key: 'ios_hls', 
    label: 'iOS HLS', 
    required: false,
    hint: 'Optional. Override for iOS app. Uses Default HLS if blank.'
  },
  { 
    key: 'android_hls', 
    label: 'Android HLS', 
    required: false,
    hint: 'Optional. Override for Android app. Uses Default HLS if blank.'
  },
  { 
    key: 'audio_hls', 
    label: 'Audio HLS', 
    required: false,
    hint: 'Optional. Audio-only stream for low-bandwidth mode.'
  },
];

// Advanced stream fields - collapsed by default
const ADVANCED_STREAM_FIELDS = [
  { 
    key: 'hls_720p', 
    label: '720p HLS', 
    required: false,
    hint: 'Optional. 720p quality override.',
    disabled: false
  },
  { 
    key: 'hls_540p', 
    label: '540p HLS', 
    required: false,
    hint: 'Optional. 540p quality override.',
    disabled: false
  },
  { 
    key: 'low_hls', 
    label: 'Low Quality HLS', 
    required: false,
    hint: 'Optional. Low bandwidth fallback stream.',
    disabled: false
  },
  { 
    key: 'mobile_hls', 
    label: 'Mobile HLS (Legacy)', 
    required: false,
    hint: 'Legacy field. Prefer iOS/Android specific overrides.',
    disabled: false
  },
];

// TV Platform stream fields - for Smart TVs and streaming devices
const TV_PLATFORM_FIELDS = [
  { 
    key: 'roku_hls', 
    label: 'Roku HLS', 
    required: false,
    hint: 'Optional. H.265/HEVC stream for Roku devices. Supports 4K.',
    disabled: false
  },
  { 
    key: 'appletv_hls', 
    label: 'Apple TV HLS', 
    required: false,
    hint: 'Optional. Override for tvOS/Apple TV. H.264 or H.265.',
    disabled: false
  },
  { 
    key: 'firetv_hls', 
    label: 'Fire TV HLS', 
    required: false,
    hint: 'Optional. Override for Amazon Fire TV devices.',
    disabled: false
  },
  { 
    key: 'androidtv_hls', 
    label: 'Android TV HLS', 
    required: false,
    hint: 'Optional. Override for Android TV (Shield, etc). Different from mobile.',
    disabled: false
  },
  { 
    key: 'samsung_hls', 
    label: 'Samsung TV HLS (Tizen)', 
    required: false,
    hint: 'Optional. Override for Samsung Smart TVs.',
    disabled: false
  },
  { 
    key: 'lg_hls', 
    label: 'LG TV HLS (WebOS)', 
    required: false,
    hint: 'Optional. Override for LG Smart TVs.',
    disabled: false
  },
  { 
    key: 'hls_4k', 
    label: '4K HLS', 
    required: false,
    hint: 'Optional. 4K/UHD stream for any compatible device. H.265 recommended.',
    disabled: false
  },
];

// All stream keys for save/load
const ALL_STREAM_KEYS = [
  ...PRIMARY_STREAM_FIELDS.map(f => f.key),
  ...ADVANCED_STREAM_FIELDS.map(f => f.key),
  ...TV_PLATFORM_FIELDS.map(f => f.key),
];

// Thumbnail preview with proper 16:9 aspect ratio and contain fit
const ThumbnailPreview = ({ src }) => {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setHasError(false);
    setIsLoading(true);
  }, [src]);

  if (!src) return null;

  return (
    <div 
      className="mt-2 relative rounded-lg border border-border bg-black/50 overflow-hidden"
      style={{ aspectRatio: '16/9' }}
    >
      {isLoading && !hasError && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      )}
      {hasError ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground">
          <ImageOff className="h-8 w-8 mb-2 opacity-50" />
          <span className="text-xs">Preview unavailable</span>
        </div>
      ) : (
        <img
          src={src}
          alt="Thumbnail Preview"
          className="absolute inset-0 w-full h-full object-contain"
          onLoad={() => setIsLoading(false)}
          onError={() => {
            setIsLoading(false);
            setHasError(true);
          }}
        />
      )}
    </div>
  );
};

// Stream URL input field component
const StreamUrlField = ({ field, value, onChange, onFocus }) => (
  <div className="space-y-1">
    <div className="flex items-center gap-2">
      <Label htmlFor={field.key} className="text-xs text-muted-foreground">
        {field.label}
        {field.required && <span className="text-destructive ml-1">*</span>}
      </Label>
    </div>
    <div className="overflow-x-auto">
      <Input
        id={field.key}
        value={value}
        onChange={(e) => onChange(field.key, e.target.value)}
        onFocus={onFocus}
        placeholder={field.required ? 'Required' : 'Optional — uses Default HLS if blank'}
        className="font-mono text-sm min-w-[500px]"
        disabled={field.disabled}
        data-testid={`camera-stream-${field.key}-input`}
      />
    </div>
    {field.hint && (
      <p className="text-[10px] text-muted-foreground/70">{field.hint}</p>
    )}
  </div>
);

const CameraCard = ({ camera, onEdit, onDelete, studioEncoders }) => {
  // Determine the effective status when set to "auto"
  const getEffectiveStatus = () => {
    try {
      if (camera.status !== 'auto') {
        return { status: camera.status || 'offline', isAuto: false };
      }
      
      // Find matching encoder from Studio
      if (camera.studio_site_id && Array.isArray(studioEncoders) && studioEncoders.length > 0) {
        const encoder = studioEncoders.find(e => e?.site_id === camera.studio_site_id);
        if (encoder) {
          // Map Studio encoder status to camera status
          // Only "online" = online, everything else (offline, error, etc.) = offline
          const isOnline = encoder.is_online === true && encoder.status !== 'error';
          return { 
            status: isOnline ? 'online' : 'offline', 
            isAuto: true, 
            encoderName: encoder.name || 'Unknown',
            encoderStatus: encoder.status // Keep original status for debugging
          };
        }
      }
      
      // No linked encoder or Studio not connected - show as offline
      return { status: 'offline', isAuto: true, noLink: !camera.studio_site_id };
    } catch (err) {
      console.error('Error determining camera status:', err);
      return { status: 'offline', isAuto: false };
    }
  };

  const effectiveStatus = getEffectiveStatus();
  const displayStatus = effectiveStatus.status || 'offline';

  return (
    <Card 
      className="group overflow-hidden border-border bg-card transition-colors hover:border-primary/50"
      data-testid={`camera-card-${camera._id || camera.id}`}
    >
      {/* Thumbnail */}
      <div className="relative aspect-video w-full overflow-hidden bg-secondary">
        {camera.thumbnail_path ? (
          <img
            src={camera.thumbnail_path}
            alt={camera.name}
            className="h-full w-full object-cover transition-transform group-hover:scale-105"
            onError={(e) => {
              e.target.src = 'https://images.unsplash.com/photo-1622096952214-9796304406aa?auto=format&fit=crop&w=800&q=60';
            }}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <Camera className="h-12 w-12 text-muted-foreground/30" />
          </div>
        )}
        
        {/* Status Badge */}
        <div className="absolute right-2 top-2 flex flex-col gap-1 items-end">
          <Badge 
            variant={displayStatus === 'online' ? 'default' : 'secondary'}
            className={
              displayStatus === 'online' 
                ? 'bg-orange-500/90 text-white' 
                : displayStatus === 'coming_soon'
                ? 'bg-blue-500/90 text-white'
                : displayStatus === 'disabled'
                ? 'bg-red-600/90 text-white'
                : 'bg-muted text-muted-foreground'
            }
          >
            {displayStatus === 'online' ? (
              <CheckCircle className="mr-1 h-3 w-3" />
            ) : displayStatus === 'coming_soon' ? (
              <Clock className="mr-1 h-3 w-3" />
            ) : displayStatus === 'disabled' ? (
              <Ban className="mr-1 h-3 w-3" />
            ) : (
              <XCircle className="mr-1 h-3 w-3" />
            )}
            {displayStatus === 'coming_soon' ? 'Coming Soon' : displayStatus === 'disabled' ? 'Disabled' : displayStatus || 'offline'}
          </Badge>
          
          {/* Auto Status Indicator */}
          {effectiveStatus.isAuto && (
            <Badge 
              variant="outline" 
              className={`text-[10px] font-medium ${effectiveStatus.noLink ? 'bg-yellow-600 border-yellow-500 text-white' : 'bg-purple-600 border-purple-400 text-white'}`}
            >
              <Zap className="mr-1 h-2.5 w-2.5" />
              {effectiveStatus.noLink ? 'Auto (No Link)' : 'Auto'}
            </Badge>
          )}
        </div>

        {/* Short Code Badge */}
        {camera.short_code && (
          <div className="absolute left-2 top-2">
            <Badge variant="outline" className="bg-background/80 font-mono text-xs">
              {camera.short_code}
            </Badge>
          </div>
        )}
      </div>

      <CardContent className="p-4">
        <div className="mb-3">
          <h3 className="font-semibold text-foreground">{camera.name}</h3>
          <p className="text-sm text-muted-foreground">{camera.location || 'No location'}</p>
        </div>

        <div className="mb-4 flex flex-wrap gap-2">
          <Badge variant="outline" className="text-xs">
            {getTierDisplayLabel(camera.min_tier, camera.min_tier_label)}
          </Badge>
          {camera.tags?.slice(0, 2).map((tag, i) => (
            <Badge key={i} variant="secondary" className="text-xs">
              {tag}
            </Badge>
          ))}
          {camera.tags?.length > 2 && (
            <Badge variant="secondary" className="text-xs">
              +{camera.tags.length - 2}
            </Badge>
          )}
        </div>

        <div className="flex gap-2">
          <Button 
            variant="secondary" 
            size="sm" 
            className="flex-1"
            onClick={() => onEdit(camera)}
            data-testid={`edit-camera-${camera._id || camera.id}`}
          >
            <Edit className="mr-1 h-4 w-4" />
            Edit
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
            onClick={() => onDelete(camera)}
            data-testid={`delete-camera-${camera._id || camera.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const CameraForm = ({ camera, onSave, onCancel, isLoading, tierOptions, studioEncoders, studioConnected }) => {
  // Use API tier_options if provided, otherwise use default
  const availableTiers = tierOptions && tierOptions.length > 0 
    ? tierOptions 
    : DEFAULT_TIER_OPTIONS;
  
  // Parse existing streams into individual fields
  const initialStreams = camera?.streams || {};
  
  // Build initial form data with all stream keys
  const buildInitialFormData = () => {
    // Normalize min_tier to string value (handle object or string input)
    const normalizedTier = normalizeTierValue(camera?.min_tier);
    
    const data = {
      name: camera?.name || '',
      short_code: camera?.short_code || '',  // Short code for tracking (e.g., FOS-EAST)
      location: camera?.location || '',
      description: camera?.description || '',
      min_tier: normalizedTier,
      status: camera?.status || 'offline',
      studio_site_id: camera?.studio_site_id || '',  // Link to Studio encoder
      thumbnail_path: camera?.thumbnail_path || '',
      tags: camera?.tags?.join(', ') || '',
      vast_tag_url: camera?.vast_tag_url || '',  // VAST tag for video ads
    };
    
    // Add all stream fields
    ALL_STREAM_KEYS.forEach(key => {
      data[key] = initialStreams[key] || '';
    });
    
    return data;
  };
  
  const [formData, setFormData] = useState(buildInitialFormData);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showTVPlatforms, setShowTVPlatforms] = useState(false);

  const handleChange = (field, value) => {
    // For min_tier, always normalize to string
    if (field === 'min_tier') {
      value = normalizeTierValue(value);
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Auto-select input content on focus
  const handleUrlFocus = (e) => {
    e.target.select();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Build streams object - only include non-empty values
    const streams = {};
    ALL_STREAM_KEYS.forEach(key => {
      const value = formData[key]?.trim();
      if (value) {
        streams[key] = value;
      }
    });

    // Ensure min_tier is always a string in the payload
    const data = {
      name: formData.name,
      short_code: formData.short_code?.trim() || null,  // Short code for tracking
      location: formData.location,
      description: formData.description,
      min_tier: normalizeTierValue(formData.min_tier),
      status: formData.status,
      studio_site_id: formData.studio_site_id?.trim() || null,  // Link to Studio encoder
      thumbnail_path: formData.thumbnail_path,
      tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
      streams,
      vast_tag_url: formData.vast_tag_url?.trim() || null,  // VAST tag for ads
    };

    onSave(data);
  };

  const setTowerDefault = () => {
    const camCode = prompt('Enter camera code (e.g., CAM001):');
    if (camCode) {
      handleChange('thumbnail_path', `${TOWER_BASE_URL}/assets/thumbs/${camCode}.jpg`);
    }
  };

  // Check if any advanced fields have values
  const hasAdvancedValues = ADVANCED_STREAM_FIELDS.some(f => formData[f.key]?.trim());
  const hasTVPlatformValues = TV_PLATFORM_FIELDS.some(f => formData[f.key]?.trim());

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2 col-span-2">
          <Label htmlFor="name">Camera Name *</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="Fostoria, Ohio"
            required
            data-testid="camera-name-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="short_code">Short Code</Label>
          <Input
            id="short_code"
            value={formData.short_code}
            onChange={(e) => handleChange('short_code', e.target.value.toUpperCase())}
            placeholder="FOS-EAST"
            className="font-mono uppercase"
            maxLength={20}
            data-testid="camera-short-code-input"
          />
          <p className="text-[10px] text-muted-foreground">For tracking in sessions</p>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="location">Location</Label>
        <Input
          id="location"
          value={formData.location}
          onChange={(e) => handleChange('location', e.target.value)}
          placeholder="East View - Riley's Railhouse"
          data-testid="camera-location-input"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder="Camera description..."
          rows={2}
          data-testid="camera-description-input"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="min_tier">Minimum Tier</Label>
          <Select 
            value={formData.min_tier} 
            onValueChange={(v) => handleChange('min_tier', v)}
          >
            <SelectTrigger data-testid="camera-tier-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {availableTiers.map((tier) => (
                <SelectItem key={tier.value} value={tier.value}>
                  {tier.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {formData.min_tier === 'conductor' && (
            <p className="text-xs text-muted-foreground">
              Conductor cameras are included with Engineer memberships.
            </p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="status">Status</Label>
          <Select 
            value={formData.status} 
            onValueChange={(v) => handleChange('status', v)}
          >
            <SelectTrigger data-testid="camera-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">
                <span className="flex items-center gap-2">
                  <Zap className="h-3 w-3 text-purple-400" />
                  Auto (Studio)
                </span>
              </SelectItem>
              <SelectItem value="online">Online</SelectItem>
              <SelectItem value="offline">Offline</SelectItem>
              <SelectItem value="coming_soon">Coming Soon</SelectItem>
              <SelectItem value="disabled">Disabled</SelectItem>
            </SelectContent>
          </Select>
          {formData.status === 'auto' && (
            <p className="text-xs text-muted-foreground">
              Status determined by Studio encoder connection.
            </p>
          )}
        </div>
      </div>

      {/* Studio Site Link - Only shown when status is "auto" */}
      {formData.status === 'auto' && (
        <div className="space-y-2 rounded-lg border border-purple-500/30 bg-purple-500/5 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Radio className="h-4 w-4 text-purple-400" />
            <Label className="text-purple-300 font-medium">Studio Integration</Label>
            {studioConnected ? (
              <Badge variant="outline" className="text-[10px] bg-green-500/20 border-green-500/50 text-green-300">
                <Wifi className="mr-1 h-2.5 w-2.5" />
                Connected
              </Badge>
            ) : (
              <Badge variant="outline" className="text-[10px] bg-red-500/20 border-red-500/50 text-red-300">
                <WifiOff className="mr-1 h-2.5 w-2.5" />
                Disconnected
              </Badge>
            )}
          </div>
          
          <Label htmlFor="studio_site_id" className="text-xs text-muted-foreground">
            Link to Studio Site
          </Label>
          {studioEncoders && studioEncoders.length > 0 ? (
            <Select 
              value={formData.studio_site_id || 'none'} 
              onValueChange={(v) => handleChange('studio_site_id', v === 'none' ? '' : v)}
            >
              <SelectTrigger data-testid="camera-studio-site-select">
                <SelectValue placeholder="Select Studio site..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">
                  <span className="text-muted-foreground">None (Unlinked)</span>
                </SelectItem>
                {studioEncoders.filter(e => e?.site_id).map((encoder) => (
                  <SelectItem key={encoder.site_id} value={encoder.site_id}>
                    <span className="flex items-center gap-2">
                      {encoder.is_online ? (
                        <span className="h-2 w-2 rounded-full bg-green-500" />
                      ) : (
                        <span className="h-2 w-2 rounded-full bg-red-500" />
                      )}
                      {encoder.name || encoder.site_id}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="space-y-2">
              <Input
                id="studio_site_id"
                value={formData.studio_site_id}
                onChange={(e) => handleChange('studio_site_id', e.target.value)}
                placeholder="Enter Studio site ID..."
                className="font-mono text-sm"
                data-testid="camera-studio-site-input"
              />
              <p className="text-[10px] text-yellow-400">
                No encoders found from Studio. Enter site ID manually or check Studio connection.
              </p>
            </div>
          )}
          <p className="text-[10px] text-muted-foreground mt-1">
            When linked, the camera's online/offline status will be determined by the Studio encoder.
          </p>
        </div>
      )}

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="thumbnail_path">Thumbnail URL</Label>
          <Button 
            type="button" 
            variant="ghost" 
            size="sm"
            onClick={setTowerDefault}
            data-testid="set-tower-default-btn"
          >
            <ImageIcon className="mr-1 h-3 w-3" />
            Set Tower Default
          </Button>
        </div>
        <Input
          id="thumbnail_path"
          value={formData.thumbnail_path}
          onChange={(e) => handleChange('thumbnail_path', e.target.value)}
          placeholder="https://tower.railstream.net/assets/thumbs/CAM001.jpg"
          className="font-mono text-sm"
          data-testid="camera-thumbnail-input"
        />
        <ThumbnailPreview src={formData.thumbnail_path} />
      </div>

      <div className="space-y-2">
        <Label htmlFor="tags">Tags (comma-separated)</Label>
        <Input
          id="tags"
          value={formData.tags}
          onChange={(e) => handleChange('tags', e.target.value)}
          placeholder="steam, vintage, station"
          data-testid="camera-tags-input"
        />
      </div>

      {/* VAST Tag URL for Ads */}
      <div className="space-y-2">
        <Label htmlFor="vast_tag_url" className="flex items-center gap-2">
          VAST Tag URL
          <span className="text-xs font-normal text-muted-foreground">(video ads)</span>
        </Label>
        <Textarea
          id="vast_tag_url"
          value={formData.vast_tag_url}
          onChange={(e) => handleChange('vast_tag_url', e.target.value)}
          onFocus={handleUrlFocus}
          placeholder="https://ads.example.com/vast?..."
          className="font-mono text-sm min-h-[80px] resize-y"
          rows={3}
          data-testid="camera-vast-tag-input"
        />
        <p className="text-xs text-muted-foreground">
          Optional. VAST XML endpoint for pre-roll/mid-roll video ads. Leave blank to disable ads.
        </p>
      </div>

      {/* Primary Stream URLs */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-base font-semibold">Stream URLs</Label>
          <span className="text-xs text-muted-foreground">
            Clients use Default HLS unless an override is set
          </span>
        </div>
        <div className="space-y-4 rounded-lg border border-border p-4 bg-secondary/20">
          {PRIMARY_STREAM_FIELDS.map(field => (
            <StreamUrlField
              key={field.key}
              field={field}
              value={formData[field.key]}
              onChange={handleChange}
              onFocus={handleUrlFocus}
            />
          ))}
        </div>
      </div>

      {/* Advanced Stream Overrides - Collapsible */}
      <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
        <CollapsibleTrigger asChild>
          <Button 
            type="button" 
            variant="ghost" 
            size="sm" 
            className="w-full justify-between text-muted-foreground hover:text-foreground"
          >
            <span className="flex items-center gap-2">
              {showAdvanced ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              Advanced Stream Overrides
            </span>
            {hasAdvancedValues && (
              <Badge variant="secondary" className="text-xs">
                {ADVANCED_STREAM_FIELDS.filter(f => formData[f.key]?.trim()).length} set
              </Badge>
            )}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="space-y-4 rounded-lg border border-border/50 p-4 bg-secondary/10 mt-2">
            <p className="text-xs text-muted-foreground">
              Quality-specific and legacy stream overrides. Leave blank to use default resolution handling.
            </p>
            {ADVANCED_STREAM_FIELDS.map(field => (
              <StreamUrlField
                key={field.key}
                field={field}
                value={formData[field.key]}
                onChange={handleChange}
                onFocus={handleUrlFocus}
              />
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* TV Platform Stream Overrides */}
      <Collapsible open={showTVPlatforms} onOpenChange={setShowTVPlatforms}>
        <CollapsibleTrigger asChild>
          <Button 
            type="button" 
            variant="ghost" 
            className="w-full flex items-center justify-between px-0 text-muted-foreground hover:text-foreground"
          >
            <span className="flex items-center gap-2">
              {showTVPlatforms ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              TV & Streaming Device Overrides
            </span>
            {hasTVPlatformValues && (
              <Badge variant="secondary" className="text-xs">
                {TV_PLATFORM_FIELDS.filter(f => formData[f.key]?.trim()).length} set
              </Badge>
            )}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="space-y-4 rounded-lg border border-border/50 p-4 bg-secondary/10 mt-2">
            <p className="text-xs text-muted-foreground">
              Platform-specific streams for Smart TVs and streaming devices. Roku supports H.265/4K, Apple TV supports H.264/H.265.
            </p>
            {TV_PLATFORM_FIELDS.map(field => (
              <StreamUrlField
                key={field.key}
                field={field}
                value={formData[field.key]}
                onChange={handleChange}
                onFocus={handleUrlFocus}
              />
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>

      <DialogFooter className="pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading} data-testid="save-camera-btn">
          {isLoading ? 'Saving...' : camera ? 'Update Camera' : 'Create Camera'}
        </Button>
      </DialogFooter>
    </form>
  );
};

export function Cameras() {
  const [cameras, setCameras] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTier, setFilterTier] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [editingCamera, setEditingCamera] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [deleteCamera, setDeleteCamera] = useState(null);
  const [tierOptions, setTierOptions] = useState(null);
  
  // Studio integration state
  const [studioStatus, setStudioStatus] = useState(null);
  const [studioError, setStudioError] = useState(null);

  const fetchCameras = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.getCameras();
      setCameras(Array.isArray(data) ? data : data.cameras || []);
      
      // Extract tier_options from API response if available
      // Production API uses 'key', local dev uses 'value'
      if (data.tier_options && Array.isArray(data.tier_options)) {
        setTierOptions(data.tier_options.map(t => ({
          value: t.key || t.value || t.id || t,
          label: t.label || t.name || t.key || t.value || t
        })));
      }
    } catch (err) {
      setError(err.message || 'Failed to load cameras');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStudioStatus = async () => {
    try {
      const data = await api.getStudioStatus();
      setStudioStatus(data);
      setStudioError(null);
    } catch (err) {
      console.error('Failed to fetch Studio status:', err);
      setStudioError(err.message);
      setStudioStatus(null);
    }
  };

  useEffect(() => {
    fetchCameras();
    fetchStudioStatus();
    
    // Poll Studio status every 60 seconds
    const studioInterval = setInterval(fetchStudioStatus, 60000);
    return () => clearInterval(studioInterval);
  }, []);

  const handleCreate = async (data) => {
    setIsSaving(true);
    try {
      await api.createCamera(data);
      toast.success('Camera created successfully');
      setShowCreateDialog(false);
      fetchCameras();
    } catch (err) {
      toast.error(err.message || 'Failed to create camera');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdate = async (data) => {
    setIsSaving(true);
    try {
      const cameraId = editingCamera._id || editingCamera.id;
      await api.updateCamera(cameraId, data);
      toast.success('Camera updated successfully');
      setEditingCamera(null);
      fetchCameras();
    } catch (err) {
      toast.error(err.message || 'Failed to update camera');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteCamera) return;
    try {
      const cameraId = deleteCamera._id || deleteCamera.id;
      await api.deleteCamera(cameraId);
      toast.success('Camera deleted successfully');
      setDeleteCamera(null);
      fetchCameras();
    } catch (err) {
      toast.error(err.message || 'Failed to delete camera');
    }
  };

  const filteredCameras = cameras.filter(cam => {
    const matchesSearch = !searchQuery || 
      cam.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      cam.location?.toLowerCase().includes(searchQuery.toLowerCase());
    // Normalize tier for comparison (handles both string and object)
    const camTier = normalizeTierValue(cam.min_tier);
    const matchesTier = filterTier === 'all' || camTier === filterTier;
    const matchesStatus = filterStatus === 'all' || cam.status === filterStatus;
    return matchesSearch && matchesTier && matchesStatus;
  });

  return (
    <div className="space-y-6" data-testid="cameras-page">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Cameras</h1>
          <p className="text-muted-foreground">Manage camera streams and settings</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Studio Connection Indicator */}
          <div 
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
              studioStatus?.connected 
                ? 'bg-green-500/20 text-green-300 border border-green-500/30' 
                : 'bg-red-500/20 text-red-300 border border-red-500/30'
            }`}
            title={studioStatus?.last_poll ? `Last poll: ${new Date(studioStatus.last_poll).toLocaleTimeString()}` : 'Studio status unknown'}
            data-testid="studio-connection-indicator"
          >
            {studioStatus?.connected ? (
              <>
                <Wifi className="h-3 w-3" />
                <span>Studio Connected</span>
                {studioStatus?.encoders?.length > 0 && (
                  <Badge variant="outline" className="ml-1 text-[10px] py-0 px-1 bg-green-500/20 border-green-500/40">
                    {studioStatus.encoders.length} encoder{studioStatus.encoders.length !== 1 ? 's' : ''}
                  </Badge>
                )}
              </>
            ) : (
              <>
                <WifiOff className="h-3 w-3" />
                <span>{studioError ? 'Studio Error' : 'Studio Disconnected'}</span>
              </>
            )}
          </div>
          
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => { fetchCameras(); fetchStudioStatus(); }}
            data-testid="refresh-cameras-btn"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          <Button 
            size="sm" 
            onClick={() => setShowCreateDialog(true)}
            data-testid="add-camera-btn"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Camera
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-border bg-card">
        <CardContent className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search cameras..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="search-cameras-input"
            />
          </div>
          <div className="flex gap-2">
            <Select value={filterTier} onValueChange={setFilterTier}>
              <SelectTrigger className="w-36" data-testid="filter-tier-select">
                <Filter className="mr-2 h-4 w-4" />
                <SelectValue placeholder="Tier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tiers</SelectItem>
                {(tierOptions || DEFAULT_TIER_OPTIONS).map((tier) => (
                  <SelectItem key={tier.value} value={tier.value}>
                    {tier.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-32" data-testid="filter-status-select">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="auto">Auto (Studio)</SelectItem>
                <SelectItem value="online">Online</SelectItem>
                <SelectItem value="offline">Offline</SelectItem>
                <SelectItem value="coming_soon">Coming Soon</SelectItem>
                <SelectItem value="disabled">Disabled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Content */}
      {isLoading ? (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="overflow-hidden border-border bg-card">
              <div className="aspect-video animate-pulse bg-muted" />
              <CardContent className="p-4">
                <div className="mb-2 h-5 w-32 animate-pulse rounded bg-muted" />
                <div className="h-4 w-24 animate-pulse rounded bg-muted" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : error ? (
        <Card className="border-destructive/50 bg-destructive/10">
          <CardContent className="flex items-center gap-4 p-6">
            <AlertCircle className="h-8 w-8 text-destructive" />
            <div className="flex-1">
              <p className="font-medium text-foreground">Failed to load cameras</p>
              <p className="text-sm text-muted-foreground">{error}</p>
            </div>
            <Button variant="outline" onClick={fetchCameras}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : filteredCameras.length === 0 ? (
        <Card className="border-border bg-card">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Camera className="mb-4 h-12 w-12 text-muted-foreground/30" />
            <p className="text-lg font-medium text-foreground">No cameras found</p>
            <p className="mb-4 text-sm text-muted-foreground">
              {searchQuery ? 'Try adjusting your search filters' : 'Add your first camera to get started'}
            </p>
            {!searchQuery && (
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Add Camera
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filteredCameras.map((camera, index) => (
            <CameraCard
              key={camera._id || camera.id || index}
              camera={camera}
              onEdit={setEditingCamera}
              onDelete={setDeleteCamera}
              studioEncoders={studioStatus?.encoders || []}
            />
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-h-[90vh] w-[960px] max-w-[95vw] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Camera</DialogTitle>
            <DialogDescription>Create a new camera stream entry</DialogDescription>
          </DialogHeader>
          <CameraForm
            onSave={handleCreate}
            onCancel={() => setShowCreateDialog(false)}
            isLoading={isSaving}
            tierOptions={tierOptions}
            studioEncoders={studioStatus?.encoders || []}
            studioConnected={studioStatus?.connected || false}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editingCamera} onOpenChange={() => setEditingCamera(null)}>
        <DialogContent className="max-h-[90vh] w-[960px] max-w-[95vw] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Camera</DialogTitle>
            <DialogDescription>Update camera settings and streams</DialogDescription>
          </DialogHeader>
          {editingCamera && (
            <CameraForm
              camera={editingCamera}
              onSave={handleUpdate}
              onCancel={() => setEditingCamera(null)}
              isLoading={isSaving}
              tierOptions={tierOptions}
              studioEncoders={studioStatus?.encoders || []}
              studioConnected={studioStatus?.connected || false}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteCamera} onOpenChange={() => setDeleteCamera(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Camera</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteCamera?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteCamera(null)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete}
              data-testid="confirm-delete-btn"
            >
              Delete Camera
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Cameras;
